import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';

import { Observable } from 'rxjs';
import {
  ListPaymentFilter,
  ListPaymentResponse,
  AllPaymentFilter,
  AllPaymentFilterAct,
  ListAllPaymentResponse,
  PaymentData,
  PaymentSummary,
} from 'src/app/class/datatype';
import { environment } from 'src/environments/environment';

@Injectable()
export class PaymentService {
  constructor(private http: HttpClient) {}

  listPayment(filter: ListPaymentFilter): Observable<ListPaymentResponse> {
    let params = new HttpParams();

    if (filter.regcode) {
      params = params.append('regcode', filter.regcode);
    }

    if (filter.type) {
      params = params.append('type', filter.type);
    }

    return this.http.get<ListPaymentResponse>(
      environment.apiUrl + 'clientapi/payment',
      { params }
    );
  }

  /**
   * Dipakai bersama oleh list, summary, dan export supaya ketiganya selalu
   * mengirim kriteria filter yang sama persis ke backend. Filter hanya dikirim
   * bila chip-nya aktif (`filterAct`), sehingga isi tabel, summary, dan nama file
   * download konsisten hanya memakai filter yang sedang aktif.
   */
  private buildAllPaymentParams(
    filter: AllPaymentFilter,
    filterAct: AllPaymentFilterAct
  ): HttpParams {
    let params = new HttpParams();

    if (filterAct.startDate && filter.startDate) {
      params = params.append('startDate', filter.startDate);
    }

    if (filterAct.endDate && filter.endDate) {
      params = params.append('endDate', filter.endDate);
    }

    if (filterAct.regcode && filter.regcode) {
      params = params.append('regcode', filter.regcode);
    }

    if (filterAct.name && filter.name) {
      params = params.append('name', filter.name);
    }

    if (filterAct.phone && filter.phone) {
      params = params.append('phone', filter.phone);
    }

    if (filterAct.type && filter.type) {
      params = params.append('type', filter.type);
    }

    if (filterAct.channel && filter.channel) {
      params = params.append('channel', filter.channel);
    }

    if (filterAct.method && filter.method) {
      params = params.append('method', filter.method);
    }

    if (filterAct.bank && filter.bank) {
      params = params.append('bank', filter.bank);
    }

    if (filter.order) {
      params = params.append('order', filter.order);
    }

    if (filterAct.nominal && filter.nominal) {
      params = params.append('nominal', filter.nominal.toString());
    }

    if (filterAct.periode && filter.periode) {
      params = params.append('periode', filter.periode);
    }

    if (filterAct.branch && filter.branch) {
      params = params.append('branch', filter.branch);
    }

    if (filterAct.affiliate && filter.affiliate) {
      params = params.append('affiliate', filter.affiliate);
    }

    if (filterAct.student_status && filter.student_status) {
      params = params.append('student_status', filter.student_status);
    }

    if (filterAct.periode_year && filter.periode_year) {
      params = params.append('periode_year', filter.periode_year.toString());
    }

    if (filterAct.periode_month && filter.periode_start_month) {
      params = params.append('periode_start_month', filter.periode_start_month.toString());
    }

    if (filterAct.periode_month && filter.periode_end_month) {
      params = params.append('periode_end_month', filter.periode_end_month.toString());
    }

    if (filterAct.periode_range && filter.periode_start) {
      params = params.append('periode_start', filter.periode_start);
    }

    if (filterAct.periode_range && filter.periode_end) {
      params = params.append('periode_end', filter.periode_end);
    }

    return params;
  }

  listAllPayment(
    filter: AllPaymentFilter,
    filterAct: AllPaymentFilterAct
  ): Observable<ListAllPaymentResponse> {
    let params = this.buildAllPaymentParams(filter, filterAct);

    if (filter.page) {
      params = params.append('page', filter.page.toString());
    }

    if (filter.per_page) {
      params = params.append('per_page', filter.per_page.toString());
    }

    if (filter.with_student) {
      params = params.append('with_student', 1);
    }

    params = params.append('download', filter.download);

    return this.http.get<ListAllPaymentResponse>(
      environment.apiUrl + 'clientapi/payment/all',
      { params }
    );
  }

  summaryAllPayment(
    filter: AllPaymentFilter,
    filterAct: AllPaymentFilterAct
  ): Observable<PaymentSummary> {
    return this.http.get<PaymentSummary>(
      environment.apiUrl + 'clientapi/payment/all/summary',
      { params: this.buildAllPaymentParams(filter, filterAct) }
    );
  }

  /**
   * Backend yang menyusun CSV-nya, dari query yang sama dengan tabel, lalu
   * di-stream sebagai satu file. Filter yang dikirim harus filter yang sedang
   * dipakai tabel, supaya isi file sama dengan yang tampil.
   */
  exportAllPayment(
    filter: AllPaymentFilter,
    filterAct: AllPaymentFilterAct
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(environment.apiUrl + 'clientapi/payment/all/export', {
      params: this.buildAllPaymentParams(filter, filterAct),
      responseType: 'blob',
      observe: 'response',
    });
  }

  listAllPaymentRetention(filter: AllPaymentFilter): Observable<ListAllPaymentResponse> {
    let params = new HttpParams();

    if (filter.startDate) {
      params = params.append('startDate', filter.startDate);
    }

    if (filter.endDate) {
      params = params.append('endDate', filter.endDate);
    }

    if (filter.regcode) {
      params = params.append('regcode', filter.regcode);
    }

    if (filter.type) {
      params = params.append('type', filter.type);
    }

    if (filter.channel) {
      params = params.append('channel', filter.channel);
    }

    if (filter.method) {
      params = params.append('method', filter.method);
    }

    if (filter.bank) {
      params = params.append('bank', filter.bank);
    }

    if (filter.order) {
      params = params.append('order', filter.order);
    }

    if (filter.nominal) {
      params = params.append('nominal', filter.nominal.toString());
    }

    if (filter.page) {
      params = params.append('page', filter.page.toString());
    }

    if (filter.per_page) {
      params = params.append('per_page', filter.per_page.toString());
    }

    if (filter.with_student) {
      params = params.append('with_student', 1);
    }
    
    params = params.append('download', filter.download);

    return this.http.get<ListAllPaymentResponse>(
      environment.apiUrl + 'clientapi/payment/retention',
      { params }
    );
  }

  savePayment(data: PaymentData): Observable<any> {
    return this.http.post<any>(environment.apiUrl + 'clientapi/payment', data);
  }

  deletePayment(id: number, regcode: number, type: string): Observable<any> {
    let params = new HttpParams();

    params = params.append('id', id);
    params = params.append('regcode', regcode);
    params = params.append('type', type);
    return this.http.delete<any>(environment.apiUrl + 'clientapi/payment', {
      params,
    });
  }

  /// Delete payment ketika student sudah berubah statusnya ke checkin
  deletePaymentActiveStudent(regcode: string, payment_id: number, student_id:number, reason?: string): Observable<any> {
    return this.http.delete<any>(environment.apiUrl + `clientapi/student/active-student-payment/${regcode}/${payment_id}/${student_id}?reason=${reason}`);
  }
  /// Delete payment ketika student sudah berubah statusnya ke checkin
  deletePaymentInstalmentStudent(regcode: string, payment_id: number): Observable<any> {
    return this.http.delete<any>(environment.apiUrl + `clientapi/student/payment-instalment/${regcode}/${payment_id}`);
  }
}
